/* ============================================================
   <reading-progress> — sticky scroll-progress bar for articles.

   Best practices applied:
   - Single passive scroll listener, throttled to one update per frame
     via requestAnimationFrame (no layout thrash).
   - Animates a transform: scaleX value, NOT width — keeps the work on
     the compositor thread.
   - Tracks a configurable target element (the article body), not the
     whole page. Progress maps from "top of article enters viewport"
     to "bottom of article reaches viewport bottom".
   - Aborts listeners cleanly on disconnect via AbortController.
   - role="progressbar" + aria-valuenow/min/max for assistive tech.
   ============================================================ */

if (!customElements.get('reading-progress')) {
  customElements.define('reading-progress', class ReadingProgress extends HTMLElement {
    connectedCallback() {
      this.setAttribute('role', 'progressbar');
      this.setAttribute('aria-valuemin', '0');
      this.setAttribute('aria-valuemax', '100');
      this.setAttribute('aria-valuenow', '0');
      this.setAttribute('aria-label', this.getAttribute('aria-label') || 'Progression de lecture');

      this.bar = this.querySelector('.reading-progress__bar');
      if (!this.bar) return;

      this.target = document.querySelector(this.getAttribute('data-target') || '.article .rte');
      if (!this.target) return;

      this.controller = new AbortController();
      const opts = { passive: true, signal: this.controller.signal };
      this.ticking = false;
      this.handleScroll = this.onScroll.bind(this);

      window.addEventListener('scroll', this.handleScroll, opts);
      window.addEventListener('resize', this.handleScroll, opts);
      // First paint
      this.update();
    }

    disconnectedCallback() {
      this.controller?.abort();
    }

    onScroll() {
      if (this.ticking) return;
      this.ticking = true;
      requestAnimationFrame(() => {
        this.update();
        this.ticking = false;
      });
    }

    update() {
      const rect = this.target.getBoundingClientRect();
      const viewportH = window.innerHeight;
      // Total scroll distance over which we want progress to fill from 0 → 1:
      // start = top of article enters viewport bottom
      // end   = bottom of article reaches viewport bottom
      const total = rect.height - viewportH + viewportH; // === rect.height; kept explicit
      // Distance scrolled past the article top, clamped 0..total
      const scrolled = Math.min(Math.max(-rect.top, 0), Math.max(total - viewportH, 1));
      const denom = Math.max(rect.height - viewportH, 1);
      const ratio = Math.min(Math.max(scrolled / denom, 0), 1);

      // GPU-friendly transform write
      this.bar.style.transform = `scaleX(${ratio})`;
      this.setAttribute('aria-valuenow', String(Math.round(ratio * 100)));
      this.classList.toggle('is-active', ratio > 0 && ratio < 1);
    }
  });
}
